// Pure scoring helpers. Every step produces a score in [0, 1]; the summary
// averages them overall, per competency and per RIASEC trait.
export const QUAL = { best: 1, ok: 0.5, bad: 0 };

// Submit payload for a picked chat/choice/call option.
export const optionResult = (o, idx) => ({
  score: QUAL[o.q],
  fx: o.fx,
  later: o.later ? [o.later] : [],
  pick: idx
});

export const clamp = (v, lo = 0, hi = 100) => Math.max(lo, Math.min(hi, v));

export const applyFx = (kpis, fx) => {
  if (!fx) return kpis;
  const next = { ...kpis };
  Object.entries(fx).forEach(([k, d]) => {
    if (k in next) next[k] = clamp(next[k] + d);
  });
  return next;
};

// Share of item pairs that are in the same relative order as `correct`.
export const scoreOrder = (order, correct) => {
  const pos = Object.fromEntries(correct.map((id, i) => [id, i]));
  let right = 0;
  let total = 0;
  for (let i = 0; i < order.length; i += 1) {
    for (let j = i + 1; j < order.length; j += 1) {
      total += 1;
      if (pos[order[i]] < pos[order[j]]) right += 1;
    }
  }
  return total ? right / total : 1;
};

export const scoreAllocate = (alloc, buckets) => {
  const diff = buckets.reduce((a, b) => a + Math.abs((alloc[b.id] || 0) - b.best), 0);
  return clamp(1 - diff / 200, 0, 1);
};

export const scoreNumeric = (value, answer, tolerance) => {
  if (!Number.isFinite(value)) return false;
  const tol = Math.max(Math.abs(answer) * tolerance, 0.01);
  return Math.abs(value - answer) <= tol;
};

// Queue: mean of the chosen option scores, plus a bonus for opening more
// urgent tickets first (pairwise, only for tickets of different urgency).
export const scoreQueue = (tickets, picks, openOrder) => {
  const opt = tickets.map((tk) => QUAL[tk.options[picks[tk.id]]?.q] ?? 0);
  const mean = opt.reduce((a, b) => a + b, 0) / (opt.length || 1);
  let right = 0;
  let total = 0;
  const urg = Object.fromEntries(tickets.map((tk) => [tk.id, tk.urgency]));
  for (let i = 0; i < openOrder.length; i += 1) {
    for (let j = i + 1; j < openOrder.length; j += 1) {
      if (urg[openOrder[i]] === urg[openOrder[j]]) continue;
      total += 1;
      if (urg[openOrder[i]] > urg[openOrder[j]]) right += 1;
    }
  }
  const orderScore = total ? right / total : 1;
  return 0.8 * mean + 0.2 * orderScore;
};

export const summarize = (pack, log) => {
  const scores = log.map((l) => l.score);
  const overall = scores.length ? scores.reduce((a, b) => a + b, 0) / scores.length : 0;
  const band =
    pack.verdict.bands.find((b) => overall >= b.min) ||
    pack.verdict.bands[pack.verdict.bands.length - 1];

  const comp = {};
  pack.competencies.forEach((c) => {
    comp[c.key] = { sum: 0, n: 0 };
  });
  const traits = {};
  log.forEach((l) => {
    const step = pack.steps.find((s) => s.id === l.stepId);
    if (!step) return;
    (step.comp || []).forEach((c, i) => {
      if (!comp[c]) return;
      const w = i === 0 ? 1 : 0.5;
      comp[c].sum += l.score * w;
      comp[c].n += w;
    });
    (step.traits || []).forEach((tr) => {
      traits[tr] = traits[tr] || { sum: 0, n: 0 };
      traits[tr].sum += l.score;
      traits[tr].n += 1;
    });
  });
  const competencies = pack.competencies.map((c) => ({
    key: c.key,
    label: c.label,
    skill: c.skill || null,
    value: comp[c.key].n ? comp[c.key].sum / comp[c.key].n : null
  }));
  const traitList = Object.entries(traits)
    .map(([key, v]) => ({ key, value: v.sum / v.n, n: v.n }))
    .sort((a, b) => b.value - a.value || b.n - a.n);
  return { overall, score100: Math.round(overall * 100), band, competencies, traits: traitList };
};
